import { assert, Test } from "oc-e2e-testrunner";
import {
  assertBestelllisteContains,
  assertEqualsBestellliste,
  assertWarnungen,
  createMauer,
  resetArrangement,
} from "./utils";
import { v2, v3 } from "oc-threejs/short3";
import { ZElement } from "../arrangement/ZElement";
import { Stromkabel } from "../arrangement/Stromkabel";
import {
  findFirstLeafe,
  flattenTreeNode,
  getHindernisPolygone,
  splitChildFromTreeImmutable,
  stringToTreeNode,
  treeNodeToString,
  VTreeNode,
} from "../arrangement/lichtleistenFunctions";
import * as Maths from "oc-commons/Maths";
import * as Maths3 from "oc-threejs/Maths3";
import * as Maths2 from "oc-threejs/Maths2";
import * as Debugs from "oc-threejs/Debugs";
import { Aufbau } from "../arrangement/Aufbau";
import { Vector2, Vector3 } from "three";

export default {
  name: "TestKabelbäume",
};

const ELEMENT_BREITE_ERWEITERT = 1.78 + 0.04;
const ELEMENT_BREITE = 1.78;

function createTrafo(position: Vector2, agt = APP.arrangement) {
  const trafo = new Aufbau(agt).fromJSON({
    Key: Commons.createUniqueIdString(),
    aufbauMeta: "Trafo",
    position: {
      x: position.x,
      y: 0,
      z: position.y,
    },
    size: {
      x: 0.4,
      y: 0.1,
      z: 0.1,
    },
    drehung: 0,
  });
  agt.aufbauten.push(trafo);
  return trafo;
}

function resetLichtleistenArrangement() {
  resetArrangement();
  APP.arrangement.bodenbefestigungAuswahlwertDefault =
    APP.arrangement.bodenbefestigungAuswahlwert_ = APP.richMetadata.findAuswahlwert(
      "Aufgeschraubt auf festem Boden"
    );
}

function setLichtleiste(element: ZElement) {
  element.setJsonPartial({
    profilAuswahlwert: "SYSTEM_Lichtleiste_Steckzaun",
    showProfil: true,
    profilPosition: 10,
  });
}

function dumpKabel(kabel: Stromkabel) {
  return {
    trafo: kabel.trafo.position,
    länge: kabel.länge,
    pfad: kabel.pfad,
    lichtAnschlüsse: kabel.lichtAnschlüsse,
    startAnschluss: kabel.startAnschluss,
    endAnschluss: kabel.endAnschluss,
  };
}

function dumpKabelShort(kabel: Stromkabel) {
  return { schlaufen: kabel.schlaufen, länge: kabel.länge };
}

function assertNoWarnings() {
  assertKabelKreuztKeineMauer();
  assertJedesKabelFührtStrom();
  if (APP.arrangement.warnungen.length > 0) {
    throw new Error(
      "Es soll keine Warnungen geben, aber: " +
        APP.arrangement.warnungen.map((w) => w.text ?? "").join("; \n")
    );
  }
}

function assertKabelKreuztKeineMauer() {
  const agt = APP.arrangement;
  const polygone = getHindernisPolygone(agt);
  for (const kabel of agt.kabel) {
    for (const strecke3 of Commons.pairwise(kabel.pfad)) {
      const strecke = strecke3.map((v) => Maths3.v3To2xz(v));
      for (const polygon of polygone) {
        if (Maths2.findLinePolygonIntersections(strecke, polygon).some(({ type }) => type == "X")) {
          console.error("Kabel kreuzt Mauer", { strecke, polygon });
          Debugs.line(strecke3, 0xff0000, 5);
          throw new Error("ein Kabel kreuzt eine Mauer");
        }
      }
    }
  }
}

/**
 * Überprüft, ob jedes Kabel in der Liste Strom führt, und wirft eine Exception, falls dies nicht der Fall ist.
 */
function assertJedesKabelFührtStrom() {
  const distanceToLineSegment = (punkt: Vector3, segment: Vector3[]) =>
    Maths2.distanceToLineSegment(
      Maths3.v3To2xz(punkt),
      segment.map((p) => Maths3.v3To2xz(p))
    );

  const kabelListe = APP.arrangement.kabel;
  const epsilon = 0.01;
  const kabelFührtStrom = new Set<Stromkabel>();

  for (const kabel of kabelListe) {
    const führtStrom = kabel.startAnschluss === "Trafo";
    if (führtStrom) {
      kabelFührtStrom.add(kabel);
    }
  }

  const kabelVerbindungen = new Map<Stromkabel, Set<Stromkabel>>();
  for (const kabel of kabelListe) {
    kabelVerbindungen.set(kabel, new Set<Stromkabel>());
  }

  function markiereVerbunden(a: Stromkabel, b: Stromkabel) {
    kabelVerbindungen.get(a).add(b);
    kabelVerbindungen.get(b).add(a);
  }

  const verbinde = (kabel1: Stromkabel, startPunkt: Vector3) => {
    for (const kabel2 of kabelListe) {
      if (kabel1 === kabel2) continue;
      for (const segment of Commons.pairwise(kabel2.pfad)) {
        const dist = distanceToLineSegment(startPunkt, segment);
        if (dist <= epsilon) {
          markiereVerbunden(kabel1, kabel2);
        }
      }
    }
  };

  // Überprüfe für jedes Kabel, ob es mit anderen Kabeln verbunden ist
  for (const kabel1 of kabelListe) {
    if (kabel1.startAnschluss === "Bus") {
      verbinde(kabel1, kabel1.pfad[0]);
    }
    if (kabel1.endAnschluss === "Bus") {
      verbinde(kabel1, kabel1.pfad.at(-1));
    }
  }

  // Verbreite führtStrom durch die Verbindungen mittels einer Breitensuche
  const queue: Stromkabel[] = [...kabelFührtStrom];
  while (queue.length > 0) {
    const aktuellesKabel = queue.shift();
    const verbundeneKabel = kabelVerbindungen.get(aktuellesKabel);
    for (const verbundenesKabel of verbundeneKabel) {
      if (!kabelFührtStrom.has(verbundenesKabel)) {
        kabelFührtStrom.add(verbundenesKabel);
        queue.push(verbundenesKabel);
      }
    }
  }

  // Überprüfe, ob jedes Kabel Strom führt
  for (const kabel of kabelListe) {
    if (!kabelFührtStrom.has(kabel)) {
      kabel.pfad.forEach((p) => {
        Debugs.point(p.clone().setY(p.y - 0.2), 0x000000, 0.02);
      });
      console.error("Kabel führt keinen Strom:", kabel);
      throw new Error("Kabel führt keinen Strom");
    }
  }
}

export const test1Lichtleiste: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [1.78]);
  APP.arrangement.lichtleistenZubehörAnzahl["3834 SYSTEM Licht Smart Control Set"] = 0;
  agt.fillIndividuellSegmentsWithElements();
  const s = agt.segmente[0];
  setLichtleiste(s.getElemente()[0]);
  agt.update3d();
  assertNoWarnings();
  assert.deepEqualsPicked(
    [
      {
        trafo: {
          x: -0.89,
          y: 0,
          z: 0.35,
        },
        länge: 0.35,
        pfad: [
          {
            x: -0.89,
            y: -0.07,
            z: 0.35,
          },
          {
            x: -0.89,
            y: -0.07,
            z: 0,
          },
        ],
        lichtAnschlüsse: [
          {
            x: -0.89,
            y: -0.07,
            z: 0,
          },
        ],
        startAnschluss: "Trafo",
        endAnschluss: null,
      },
    ],
    agt.kabel.map(dumpKabel)
  );
  assertEqualsBestellliste(
    [
      {
        ArtikelID: "2143",
        Anzahl: 1,
      },
      {
        ArtikelID: "2341",
        Anzahl: 2,
      },
      {
        ArtikelID: "2316",
        Anzahl: 2,
      },
      {
        ArtikelID: "1398",
        Anzahl: 1,
      },
      {
        ArtikelID: "1272",
        Anzahl: 1,
      },
      {
        ArtikelID: "3819",
        Anzahl: 1,
      },
      {
        ArtikelID: "3826",
        Anzahl: 1,
      },
      {
        ArtikelID: "3821",
        Anzahl: 1,
      },
    ],
    agt.bestellliste
  );
};

export const test1LichtleisteTrafoWeiterWeg: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [1.78]);
  APP.arrangement.lichtleistenZubehörAnzahl["3834 SYSTEM Licht Smart Control Set"] = 0;
  agt.fillIndividuellSegmentsWithElements();
  const s = agt.segmente[0];
  setLichtleiste(s.getElemente()[0]);
  agt.update3d();
  agt.kabel[0].trafo.position.setZ(2);
  agt.update3d();
  assertNoWarnings();
  assert.deepEqualsPicked(
    [
      {
        trafo: {
          x: -0.89,
          y: 0,
          z: 2,
        },
        länge: 2,
        pfad: [
          {
            x: -0.89,
            y: -0.07,
            z: 2,
          },
          {
            x: -0.89,
            y: -0.07,
            z: 0,
          },
        ],
        lichtAnschlüsse: [
          {
            x: -0.89,
            y: -0.07,
            z: 0,
          },
        ],
        startAnschluss: "Trafo",
        endAnschluss: null,
      },
    ],
    agt.kabel.map(dumpKabel)
  );
  assertEqualsBestellliste(
    [
      {
        ArtikelID: "2143",
        Anzahl: 1,
      },
      {
        ArtikelID: "2341",
        Anzahl: 2,
      },
      {
        ArtikelID: "2316",
        Anzahl: 2,
      },
      {
        ArtikelID: "1398",
        Anzahl: 1,
      },
      {
        ArtikelID: "1272",
        Anzahl: 1,
      },
      {
        ArtikelID: "3819",
        Anzahl: 1,
      },
      {
        ArtikelID: "3826",
        Anzahl: 1,
      },
      {
        ArtikelID: "3821",
        Anzahl: 1,
      },
    ],
    agt.bestellliste
  );
};

export const test1LichtleisteTrafoRechts: Test = async () => {
  resetLichtleistenArrangement();

  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [1.78]);
  APP.arrangement.lichtleistenZubehörAnzahl["3834 SYSTEM Licht Smart Control Set"] = 0;
  agt.fillIndividuellSegmentsWithElements();
  const s = agt.segmente[0];
  setLichtleiste(s.getElemente()[0]);
  agt.update3d();
  agt.kabel[0].trafo.position.setZ(2).setX(3);
  agt.update3d();
  assertNoWarnings();
  assert.deepEqualsPicked(
    [
      {
        trafo: {
          x: 3,
          y: 0,
          z: 2,
        },
        länge: 2.9072,
        pfad: [
          {
            x: 3,
            y: -0.07,
            z: 2,
          },
          {
            x: 0.89,
            y: -0.07,
            z: 0,
          },
        ],
        lichtAnschlüsse: [
          {
            x: 0.89,
            y: -0.07,
            z: 0,
          },
        ],
        startAnschluss: "Trafo",
        endAnschluss: null,
      },
    ],
    agt.kabel.map(dumpKabel)
  );
  assertEqualsBestellliste(
    [
      {
        ArtikelID: "2143",
        Anzahl: 1,
      },
      {
        ArtikelID: "2341",
        Anzahl: 2,
      },
      {
        ArtikelID: "2316",
        Anzahl: 2,
      },
      {
        ArtikelID: "1398",
        Anzahl: 1,
      },
      {
        ArtikelID: "1272",
        Anzahl: 1,
      },
      {
        ArtikelID: "3819",
        Anzahl: 1,
      },
      {
        ArtikelID: "3826",
        Anzahl: 1,
      },
      {
        ArtikelID: "3821",
        Anzahl: 1,
      },
    ],
    agt.bestellliste
  );
};

export const test15Lichtleisten: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  // FIXME warum muss 0.17 abgezogen werden? Das war vorher nicht - unklar, warum!
  // Wenn die 0.17 nicht abgezogen werden, dann passt das Standardelement nicht mehr, und es muss
  // ein Element in zwei Schmale unterteilt werden
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.grundriss.setStandardForm("Strecke", [ELEMENT_BREITE_ERWEITERT * 15]);
  // agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  const s = agt.segmente[0];
  agt.update3d();
  s.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
  // assert.deepEquals([{ schlaufen: 0, länge: 25.83 }], agt.kabel.map(dumpKabelShort));
  assert.equals(
    15,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
};

export const test15LichtleistenTrafoViertelVonLinks: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.grundriss.setStandardForm("Strecke", [ELEMENT_BREITE * 15]);
  // agt.fillIndividuellSegmentsWithElements();
  const s = agt.segmente[0];
  agt.update3d();
  s.getElemente().forEach(setLichtleiste);
  agt.update3d();
  const elemente = s.getElemente();
  const viertel = Math.floor(elemente.length / 4);
  const x = s.getWorldPositionOfX(s.getX(elemente.at(viertel), 0)).x;
  agt.kabel[0].trafo.position.setX(x);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 0,
        länge: 23.14,
      },
      {
        schlaufen: 0,
        länge: 0.35,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
  assert.equals(
    14,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
  assertEqualsBestellliste(
    [
      {
        ArtikelID: "2143",
        Anzahl: 15,
      },
      {
        ArtikelID: "2341",
        Anzahl: 16,
      },
      {
        ArtikelID: "2316",
        Anzahl: 16,
      },
      {
        ArtikelID: "1398",
        Anzahl: 2,
      },
      {
        ArtikelID: "1272",
        Anzahl: 8,
      },
      {
        ArtikelID: "3819",
        Anzahl: 15,
      },
      {
        ArtikelID: "3820",
        Anzahl: 1,
      },
      {
        ArtikelID: "3822",
        Anzahl: 1,
      },
      {
        ArtikelID: "3823",
        Anzahl: 1,
      },
      {
        ArtikelID: "3834",
        Anzahl: 1,
      },
    ],
    agt.bestellliste
  );
};

export const test15LichtleistenTrafoViertelVonLinksWeiterWeg: Test = async () => {
  resetLichtleistenArrangement();

  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.grundriss.setStandardForm("Strecke", [ELEMENT_BREITE * 15]);
  // agt.fillIndividuellSegmentsWithElements();
  const s = agt.segmente[0];
  agt.update3d();
  s.getElemente().forEach(setLichtleiste);
  agt.update3d();
  const elemente = s.getElemente();
  const viertel = Math.floor(elemente.length / 4);
  const x = s.getWorldPositionOfX(s.getX(elemente.at(viertel), 0)).x;
  agt.kabel[0].trafo.position.setX(x).setZ(2);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 1,
        länge: 28.7001,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
  assert.equals(
    14,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
  assertEqualsBestellliste(
    [
      {
        ArtikelID: "2143",
        Anzahl: 15,
      },
      {
        ArtikelID: "2341",
        Anzahl: 16,
      },
      {
        ArtikelID: "2316",
        Anzahl: 16,
      },
      {
        ArtikelID: "1398",
        Anzahl: 2,
      },
      {
        ArtikelID: "1272",
        Anzahl: 8,
      },
      {
        ArtikelID: "3819",
        Anzahl: 15,
      },
      {
        ArtikelID: "3820",
        Anzahl: 1,
      },
      {
        ArtikelID: "3822",
        Anzahl: 1,
      },
      {
        ArtikelID: "3834",
        Anzahl: 1,
      },
    ],
    agt.bestellliste
  );
};

export const test15LichtleistenTrafoViertelVonRechts: Test = async () => {
  resetLichtleistenArrangement();

  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.grundriss.setStandardForm("Strecke", [ELEMENT_BREITE * 15]);
  // agt.fillIndividuellSegmentsWithElements();
  const s = agt.segmente[0];
  agt.update3d();
  s.getElemente().forEach(setLichtleiste);
  agt.update3d();
  const elemente = s.getElemente();
  const viertel = Math.floor(elemente.length / 4);
  const x = s.getWorldPositionOfX(s.getX(elemente.at(-viertel), 0)).x;
  agt.kabel[0].trafo.position.setX(x);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 0,
        länge: 23.14,
      },
      {
        schlaufen: 0,
        länge: 0.35,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
  assert.equals(
    14,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
  assertEqualsBestellliste(
    [
      {
        ArtikelID: "2143",
        Anzahl: 15,
      },
      {
        ArtikelID: "2341",
        Anzahl: 16,
      },
      {
        ArtikelID: "2316",
        Anzahl: 16,
      },
      {
        ArtikelID: "1398",
        Anzahl: 2,
      },
      {
        ArtikelID: "1272",
        Anzahl: 8,
      },
      {
        ArtikelID: "3819",
        Anzahl: 15,
      },
      {
        ArtikelID: "3820",
        Anzahl: 1,
      },
      {
        ArtikelID: "3822",
        Anzahl: 1,
      },
      {
        ArtikelID: "3823",
        Anzahl: 1,
      },
      {
        ArtikelID: "3834",
        Anzahl: 1,
      },
    ],
    agt.bestellliste
  );
};

export const test15LichtleistenTrafoViertelVonRechtsWeiterWeg: Test = async () => {
  resetLichtleistenArrangement();

  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.grundriss.setStandardForm("Strecke", [ELEMENT_BREITE * 15]);
  // agt.fillIndividuellSegmentsWithElements();
  const s = agt.segmente[0];
  agt.update3d();
  s.getElemente().forEach(setLichtleiste);
  agt.update3d();
  const elemente = s.getElemente();
  const viertel = Math.floor(elemente.length / 4);
  const x = s.getWorldPositionOfX(s.getX(elemente.at(-viertel), 0)).x;
  agt.kabel[0].trafo.position.setX(x).setZ(2);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 1,
        länge: 28.7001,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
  assert.equals(
    14,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
  assertEqualsBestellliste(
    [
      {
        ArtikelID: "2143",
        Anzahl: 15,
      },
      {
        ArtikelID: "2341",
        Anzahl: 16,
      },
      {
        ArtikelID: "2316",
        Anzahl: 16,
      },
      {
        ArtikelID: "1398",
        Anzahl: 2,
      },
      {
        ArtikelID: "1272",
        Anzahl: 8,
      },
      {
        ArtikelID: "3819",
        Anzahl: 15,
      },
      {
        ArtikelID: "3820",
        Anzahl: 1,
      },
      {
        ArtikelID: "3822",
        Anzahl: 1,
      },
      {
        ArtikelID: "3834",
        Anzahl: 1,
      },
    ],
    agt.bestellliste
  );
};

export const test16Lichtleisten: Test = async () => {
  resetLichtleistenArrangement();

  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.grundriss.setStandardForm("Strecke", [ELEMENT_BREITE * 16]);
  // agt.fillIndividuellSegmentsWithElements();
  const s = agt.segmente[0];
  agt.update3d();
  s.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
  assert.equals(2, agt.trafoTrees.length);
  assert.equals(
    16,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
  assert.deepEquals(
    [
      {
        schlaufen: 0,
        länge: 25.27,
      },
      {
        schlaufen: 0,
        länge: 0.35,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
};

export const test15LichtleistenJedes3teElement: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.grundriss.setStandardForm("Strecke", [ELEMENT_BREITE_ERWEITERT * 15 * 3]);
  agt.update3d();
  const s = agt.segmente[0];
  const lichtleistenElemente = s.getElemente().filter((e, i) => i % 3 == 1);
  lichtleistenElemente.forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
  assert.equals(3, agt.kabel.length);
  assert.equals(
    lichtleistenElemente.length,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
};

export const testLichtleistenRechteck3x3: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  const sideLength = ELEMENT_BREITE_ERWEITERT * 3;
  agt.grundriss.setStandardForm("Rechteck", [sideLength, sideLength]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 0,
        länge: 18.2,
      },
      {
        schlaufen: 0,
        länge: 0.35,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
};

export const testLichtleistenRechteck4x4: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  const sideLength = ELEMENT_BREITE_ERWEITERT * 4;
  agt.grundriss.setStandardForm("Rechteck", [sideLength, sideLength]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 0,
        länge: 25.48,
      },
      {
        schlaufen: 0,
        länge: 0.35,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
};

export const testLichtleistenRechteck4x4TrafoWeiterWeg: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  const sideLength = ELEMENT_BREITE_ERWEITERT * 4;
  agt.grundriss.setStandardForm("Rechteck", [sideLength, sideLength]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  agt.kabel[0].trafo.position.setX(0).setZ(0);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 1,
        länge: 30,
      },
      {
        schlaufen: 0,
        länge: 8.22,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
};

export const testLichtleistenRechteck5x5: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  const sideLength = ELEMENT_BREITE_ERWEITERT * 5;
  agt.grundriss.setStandardForm("Rechteck", [sideLength, sideLength]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 0,
        länge: 25.48,
      },
      {
        schlaufen: 0,
        länge: 0.35,
      },
      {
        schlaufen: 0,
        länge: 5.81,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
};

export const testSplitChildFromTreeImmutable: Test = async () => {
  const s = "a(b(de)c)";
  const t = stringToTreeNode(s);
  assert.equals(s, treeNodeToString(t));
  const l = findFirstLeafe(t);
  assert.equals("d", treeNodeToString(l));
  const b = (flattenTreeNode(t) as VTreeNode[]).find((n) => n.value == "b");
  assert.equals(
    "a(bc),b(de)",
    splitChildFromTreeImmutable(t, b)
      .map((a) => treeNodeToString(a))
      .join(",")
  );
};

export const testLichtleistenStreckeMitLücken: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  const sideLength = ELEMENT_BREITE_ERWEITERT * 8;
  agt.grundriss.setStandardForm("Strecke", [sideLength]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt
    .getElemente()
    .filter((e, i) => i % 2 == 1)
    .forEach((e) => e.uiRemove());
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 0,
        länge: 11.27,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
  assert.equals(
    4,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
};

export const testLichtleistenStreckeMitMauern: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  const sideLength = ELEMENT_BREITE_ERWEITERT * 10;
  agt.grundriss.setStandardForm("Strecke", [sideLength]);
  agt.update3d();
  for (let x = -sideLength / 2; x < sideLength / 2; x += ELEMENT_BREITE_ERWEITERT - 0.2) {
    const mauer = createMauer({
      position: v3(x, 0, 0),
      size: v3(0.5, 2.1, 0.5),
    });
    x += mauer.size.x;
  }
  agt.update3d();
  const element = agt.createDefaultElement();
  setLichtleiste(element);
  agt.getEmpties().forEach((e) =>
    e.fillWithElements(element, {
      allowMassElement: true,
      minimalOneElement: false,
      maximumNumberOfElements: 1,
    })
  );
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 0,
        länge: 21.6706,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
  assert.equals(
    9,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
};

export const testLichtleistenLForm4x4x4: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  const sideLength = ELEMENT_BREITE_ERWEITERT * 4;
  agt.grundriss.setStandardForm("LForm", [sideLength, sideLength, sideLength, sideLength]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 0,
        länge: 25.48,
      },
      {
        schlaufen: 0,
        länge: 0.35,
      },
      {
        schlaufen: 0,
        länge: 25.83,
      },
      {
        schlaufen: 0,
        länge: 0.35,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
};

export const testLichtleistenLForm5x5x5: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  const sideLength = ELEMENT_BREITE_ERWEITERT * 5;
  agt.grundriss.setStandardForm("LForm", [sideLength, sideLength, sideLength, sideLength]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
  assert.deepEquals(
    [
      {
        schlaufen: 0,
        länge: 25.83,
      },
      {
        schlaufen: 0,
        länge: 25.83,
      },
      {
        schlaufen: 0,
        länge: 14.91,
      },
    ],
    agt.kabel.map(dumpKabelShort)
  );
};

export const testLichtleistenKreuz: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  const sideLength = ELEMENT_BREITE_ERWEITERT * 2;
  agt.grundriss.setStrecken([
    [v2(-sideLength, 0), v2(sideLength, 0)],
    [v2(0, -sideLength), v2(0, sideLength)],
  ]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
};

export const testLichtleistenGitter2x2: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  const sideLength = ELEMENT_BREITE * 2;
  const strecken = [];
  for (let i = 0; i <= sideLength; i += ELEMENT_BREITE) {
    strecken.push([v2(0, i), v2(sideLength, i)]);
    strecken.push([v2(i, 0), v2(i, sideLength)]);
  }
  agt.grundriss.setStrecken(strecken);
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assert.equals(12, agt.getElemente().filter((e) => e.hasLichtleisten).length);
  assertNoWarnings();
};

export const testLichtleistenGitter3x3: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  const sideLength = ELEMENT_BREITE * 3;
  const strecken = [];
  for (let i = 0; i <= sideLength; i += ELEMENT_BREITE) {
    strecken.push([v2(0, i), v2(sideLength, i)]);
    strecken.push([v2(i, 0), v2(i, sideLength)]);
  }
  agt.grundriss.setStrecken(strecken);
  // update-Cascade siehe testLichtleistenGitter2x2
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assert.equals(24, agt.getElemente().filter((e) => e.hasLichtleisten).length);
  assertNoWarnings();
};

export const testLichtleistenGitter4x4: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  const sideLength = ELEMENT_BREITE * 4;
  const strecken = [];
  for (let i = 0; i <= sideLength; i += ELEMENT_BREITE) {
    strecken.push([v2(0, i), v2(sideLength, i)]);
    strecken.push([v2(i, 0), v2(i, sideLength)]);
  }
  agt.grundriss.setStrecken(strecken);
  // update-Cascade siehe testLichtleistenGitter2x2
  agt.update3d();
  assert.equals(40, agt.getElemente().length);
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assert.equals(40, agt.getElemente().filter((e) => e.hasLichtleisten).length);
  assertNoWarnings();
};

export const testLichtleistenGitter5x5: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  const sideLength = ELEMENT_BREITE * 5;
  const strecken = [];
  for (let i = 0; i <= sideLength; i += ELEMENT_BREITE) {
    strecken.push([v2(0, i), v2(sideLength, i)]);
    strecken.push([v2(i, 0), v2(i, sideLength)]);
  }
  agt.grundriss.setStrecken(strecken);
  // update-Cascade siehe testLichtleistenGitter2x2
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assert.equals(60, agt.getElemente().filter((e) => e.hasLichtleisten).length);
  assertNoWarnings();
};

export const testLichtleistenSchachteln: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  const sideLength = ELEMENT_BREITE * 3;
  const strecken = [];
  for (let i = 1; i <= sideLength; i += ELEMENT_BREITE) {
    strecken.push([v2(-i, -i), v2(i, -i)]);
    strecken.push([v2(i, -i), v2(i, i)]);
    strecken.push([v2(i, i), v2(-i, i)]);
    strecken.push([v2(-i, i), v2(-i, -i)]);
  }
  agt.grundriss.setStrecken(strecken);
  // agt.update3d();
  // agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  agt.update3d();
  assertNoWarnings();
};

export const testLichtleisteMauerMaze: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  const size = 15;
  createMazeWalls(generateMaze(size, size), ELEMENT_BREITE_ERWEITERT);
  agt.grundriss.setStrecken([
    [
      v2((size / 3) * ELEMENT_BREITE_ERWEITERT, (size - 1) * ELEMENT_BREITE_ERWEITERT),
      v2((size - 1) * ELEMENT_BREITE_ERWEITERT, (size / 3) * ELEMENT_BREITE_ERWEITERT),
    ],
  ]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  const trafo = createTrafo(v2(-5, 2));
  agt.update3d();
  assertNoWarnings();
};

export const testLichtleistenÜberKomplexesBeispielVonHand: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.grundriss.fromJSON({
    strecken: [
      [
        {
          x: -2.5,
          y: -2.5,
        },
        {
          x: -2.5,
          y: 2.5,
        },
      ],
      [
        {
          x: -2.5,
          y: 2.5,
        },
        {
          x: 2.5,
          y: 2.5,
        },
      ],
      [
        {
          x: 3.5,
          y: -3.5,
        },
        {
          x: -2.5,
          y: -2.5,
        },
      ],
      [
        {
          x: 3.5,
          y: -3.5,
        },
        {
          x: 1,
          y: -0.5,
        },
      ],
      [
        {
          x: 1,
          y: -0.5,
        },
        {
          x: 2.5,
          y: 2.5,
        },
      ],
      [
        {
          x: -9,
          y: 6,
        },
        {
          x: 11,
          y: 6,
        },
      ],
      [
        {
          x: 11,
          y: 6,
        },
        {
          x: 11,
          y: -1.8571428571428568,
        },
      ],
      [
        {
          x: 11,
          y: -1.8571428571428568,
        },
        {
          x: 11,
          y: -8,
        },
      ],
      [
        {
          x: 11,
          y: -1.8571428571428568,
        },
        {
          x: 13.5,
          y: 1,
        },
      ],
      [
        {
          x: 11,
          y: -8,
        },
        {
          x: 5.625,
          y: -8,
        },
      ],
      [
        {
          x: 13.5,
          y: 1,
        },
        {
          x: 13.5,
          y: -11,
        },
      ],
      [
        {
          x: 5.625,
          y: -8,
        },
        {
          x: 11,
          y: -1.8571428571428568,
        },
      ],
      [
        {
          x: 5.625,
          y: -8,
        },
        {
          x: -9,
          y: -8,
        },
      ],
      [
        {
          x: 13.5,
          y: -11,
        },
        {
          x: 3,
          y: -11,
        },
      ],
      [
        {
          x: -9,
          y: -8,
        },
        {
          x: -9,
          y: 6,
        },
      ],
      [
        {
          x: 3,
          y: -11,
        },
        {
          x: 5.625,
          y: -8,
        },
      ],
      [
        {
          x: 3,
          y: -11,
        },
        {
          x: -12.5,
          y: -11,
        },
      ],
      [
        {
          x: -12.5,
          y: -11,
        },
        {
          x: -12.5,
          y: 10,
        },
      ],
      [
        {
          x: -12.5,
          y: 10,
        },
        {
          x: 13.5,
          y: 10,
        },
      ],
      [
        {
          x: 13.5,
          y: 10,
        },
        {
          x: 13.5,
          y: 1,
        },
      ],
    ],
  });
  agt.aufbauten = [
    {
      Key: "fn5f5wc1p97z",
      aufbauMeta: "Trafo",
      position: {
        x: -1.5,
        y: 0,
        z: -1.5,
      },
      size: {
        x: 0.4,
        y: 0.1,
        z: 0.1,
      },
      drehung: 0,
    },
    {
      Key: "hnbm5wcxjgot",
      aufbauMeta: "Mauer",
      position: {
        x: -2.116676016201718,
        y: 0,
        z: 1.8629558560485826,
      },
      size: {
        x: 2.9717085181230214,
        y: 2,
        z: 2.6241167938765226,
      },
      drehung: 0,
    },
    {
      Key: "6omg5wcxkbpa",
      aufbauMeta: "Mauer",
      position: {
        x: 1.526455159337468,
        y: 0,
        z: -0.4274444587958186,
      },
      size: {
        x: 3.052631787373337,
        y: 2,
        z: 1.5460346342812386,
      },
      drehung: 0,
    },
    {
      Key: "ulo5wesp6kd",
      aufbauMeta: "Haus",
      position: {
        x: -3.000162630949605,
        y: 0,
        z: -7.047776901496228,
      },
      size: {
        x: 8,
        y: 4,
        z: 6,
      },
      drehung: 0,
    },
    {
      Key: "88wu5wespuq6",
      aufbauMeta: "Garage",
      position: {
        x: -8.539229197429854,
        y: 0,
        z: -2.9277247213951796,
      },
      size: {
        x: 4,
        y: 3,
        z: 4,
      },
      drehung: 0,
    },
    {
      Key: "7m2t5wesqsyl",
      aufbauMeta: "Trafo",
      position: {
        x: -8,
        y: 0,
        z: 7,
      },
      size: {
        x: 0.4,
        y: 0.1,
        z: 0.1,
      },
      drehung: 0,
    },
    {
      Key: "bmqe5wesqsyl",
      aufbauMeta: "Trafo",
      position: {
        x: 12,
        y: 0,
        z: -0.8571428571428568,
      },
      size: {
        x: 0.4,
        y: 0.1,
        z: 0.1,
      },
      drehung: 0,
    },
    {
      Key: "eqmq5wesqsyl",
      aufbauMeta: "Trafo",
      position: {
        x: 14.5,
        y: 0,
        z: -10,
      },
      size: {
        x: 0.4,
        y: 0.1,
        z: 0.1,
      },
      drehung: 0,
    },
    {
      Key: "7p1r5wesqsyl",
      aufbauMeta: "Trafo",
      position: {
        x: -8,
        y: 0,
        z: -7,
      },
      size: {
        x: 0.4,
        y: 0.1,
        z: 0.1,
      },
      drehung: 0,
    },
    {
      Key: "9phr5wesqsyl",
      aufbauMeta: "Trafo",
      position: {
        x: -11.5,
        y: 0,
        z: 11,
      },
      size: {
        x: 0.4,
        y: 0.1,
        z: 0.1,
      },
      drehung: 0,
    },
    {
      Key: "6s765wesqsyl",
      aufbauMeta: "Trafo",
      position: {
        x: 10.208333333333334,
        y: 0,
        z: -7,
      },
      size: {
        x: 0.4,
        y: 0.1,
        z: 0.1,
      },
      drehung: 0,
    },
  ].map((x) => new Aufbau(agt).fromJSON(x));
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.getElemente().forEach(setLichtleiste);
  const doppeltor = APP.richMetadata.findAuswahlwert("Doppeltor_Easy");
  agt.changeZaunElemente(
    { massAuswahlwert: doppeltor },
    [20].map((i) => agt.getElemente()[i])
  );
  const einzeltor = APP.richMetadata.findAuswahlwert("Einzeltor_Easy");
  agt.changeZaunElemente(
    { massAuswahlwert: einzeltor },
    [80].map((i) => agt.getElemente()[i])
  );
  agt.update3d();
  assertWarnungen(agt, [
    "Torflügel kollidiert im Öffnungsbereich mit anderen Elementen",
    "Dieses Teil kollidiert mit dem Öffnungsbereich eines Torflügels",
  ]);
};

export const testLichtpfostenkappeUniversalMetall: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.grundriss.setStandardForm("Strecke", [ELEMENT_BREITE * 10]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  const s = agt.segmente[0];
  s.getPfosten().forEach((p) => {
    p.setJsonPartial({
      pfostenartAuswahlwert: "Klemmpfosten",
      pfostenkappeAuswahlwert: "3840 SYSTEM Licht Pfostenkappe Universal",
    });
  });
  agt.update3d();
  assertNoWarnings();
  assertBestelllisteContains(
    {
      ArtikelID: "3840",
      Anzahl: 11,
    },
    agt.bestellliste
  );
  assert.equals(
    11,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
};

export const testLichtpfostenkappeSteckpfostenMetall: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.verteilungsstrategieAuswahlwert_ = APP.richMetadata.findAuswahlwert("Gleichmässig");
  agt.grundriss.setStandardForm("Strecke", [ELEMENT_BREITE * 10]);
  agt.update3d();
  agt.fillIndividuellSegmentsWithElements();
  const s = agt.segmente[0];
  s.getPfosten().forEach((p) => {
    p.setJsonPartial({
      pfostenartAuswahlwert: "Steckpfosten",
      pfostenkappeAuswahlwert: "3841 SYSTEM Licht Pfostenkappe für Steckpfosten",
    });
  });
  agt.update3d();
  assertNoWarnings();
  assertBestelllisteContains(
    {
      ArtikelID: "3841",
      Anzahl: 11,
    },
    agt.bestellliste
  );
  assert.equals(
    11,
    _.sumBy(agt.kabel, (k) => k.lichtAnschlüsse.length)
  );
};

function generateMaze(
  width: number,
  height: number,
  prng = Maths.createPseudoRandomGeneratorMulberry32(2)
) {
  const maze = Array.from({ length: width }, () => Array(height).fill(1));
  const isInBounds = (x: number, z: number) => x > 0 && x < width - 1 && z > 0 && z < height - 1;
  const shuffle = <T>(arr: T[]): T[] => arr.sort(() => prng() - 0.5);
  const directions = [
    [-2, 0],
    [2, 0],
    [0, -2],
    [0, 2],
  ];
  const removeWall = (x: number, z: number) => {
    maze[x][z] = 0;
  };
  const carvePath = (x: number, z: number): void => {
    maze[x][z] = 0;
    shuffle(directions).forEach(([dx, dz]) => {
      const nx = x + dx,
        nz = z + dz;
      if (isInBounds(nx, nz) && maze[nx][nz] === 1) {
        removeWall((x + nx) / 2, (z + nz) / 2);
        carvePath(nx, nz);
      }
    });
  };
  removeWall(0, 1);
  carvePath(1, 1);
  return maze;
}

function createMazeWalls(maze: number[][], gridSize: number) {
  return maze.forEach((row, x) =>
    row.forEach(
      (cell, z) =>
        cell === 1 &&
        createMauer({
          position: v3(x * gridSize, 0, z * gridSize),
          size: v3(gridSize, 2.1, gridSize),
        })
    )
  );
}

export const testLichtleistenKeineUnnötigeSchlaufe: Test = async () => {
  resetLichtleistenArrangement();
  const agt = APP.arrangement;
  agt.fromJSON({
    inspiration: "Inspiration AROS Vorgarten Anthrazit",
    zaunart: "Vorgarten",
    serie: "AROS Aluminiumzaun",
    grundriss: {
      ecken: [
        {
          id: 3,
          punkt: {
            x: -6.4825,
            y: 0,
          },
        },
        {
          id: 4,
          punkt: {
            x: 6.4825,
            y: 0,
          },
        },
      ],
      kanten: [
        {
          ecken: [3, 4],
        },
      ],
    },
    segmente: [
      {
        breite: 12.965,
        partsAndPfosten: [
          {
            type: "Pfosten",
            x0: -1.235,
            pfostenfarbeAuswahlwert: "Anthrazit",
            pfostenfarbeAuswahlwertIndividualisiert: false,
            bodenbefestigungAuswahlwert: "Aufgeschraubt auf festem Boden",
            bodenbefestigungAuswahlwertIndividualisiert: false,
            elementhalterAuswahlwert: "2096 Elementhalter",
            elementhalterAuswahlwertIndividualisiert: false,
            pfostenartAuswahlwert: "Metallpfosten",
            pfostenartAuswahlwertIndividualisiert: false,
            montageadapterAuswahlwert: "Montageadapter_ohne",
            verbundankerAuswahlwert: "Verbundanker-Set_mit",
            ummantelungAuswahlwert: "Ummantelung_ohne",
            pfostenkappeAuswahlwert: "Pfostenkappe_inklusiv",
            ankerInnen: false,
            ausgleichGefälle: false,
          },
          {
            type: "Element",
            breite: 2.38,
            höhe: 0.95,
            snap: 1,
            isBreiteKonfigurierbar: false,
            x0: -1.16,
            füllungAuswahlwert: "AROS Vorgarten Anthrazit",
            füllungAuswahlwertIndividualisiert: false,
            gehflügelAuswahlwert: "Gehflügel_links",
            drückerAuswahlwert: "Drücker_Knauf_edelstahl",
            profilzylinderAuswahlwert: "Profilzylinder_mit",
            öffnungAuswahlwert: "Öffnung_nach_innen",
            anschlagAuswahlwert: "Anschlag_links",
            schieberichtungAuswahlwert: "Schieberichtung_links",
            schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
            anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilAuswahlwertIndividualisiert: false,
            zubehörAnzahl: {
              "Elektrisches Bodenverriegelungs-Set": 0,
              Handsender: 0,
              "Handsender Design": 0,
              Schlüsselschalter: 0,
              "Funk-Codeschloss": 0,
              "Bluetooth Schalter": 0,
              "Connect Gateway": 0,
              Lichtschranke: 0,
              "Techniker Key": 0,
            },
            toröffnerAuswahlwert: "Toröffner_ohne",
            torschließerAuswahlwert: "Torschließer_ohne",
            form: "Rechteck",
            massAuswahlwert: "240x95_Rechteck_Easy",
            profilPosition: null,
            profilPositionIndividualisiert: false,
            showProfil: false,
            anschlussAufRechts: false,
            gehflügelbreite: 0,
            standflügelbreite: 0,
            torIstFertig: false,
            elementNr: 0,
            isStandardMass: true,
            detailSerie: "AROS Vorgarten",
            anMauer: "AnMauer_nein",
            isMasselement: false,
            isBestellseite: false,
            elementID: 0,
          },
          {
            type: "Pfosten",
            x0: 1.225,
            pfostenfarbeAuswahlwert: "Anthrazit",
            pfostenfarbeAuswahlwertIndividualisiert: false,
            bodenbefestigungAuswahlwert: "Aufgeschraubt auf festem Boden",
            bodenbefestigungAuswahlwertIndividualisiert: false,
            elementhalterAuswahlwert: "2096 Elementhalter",
            elementhalterAuswahlwertIndividualisiert: false,
            pfostenartAuswahlwert: "Metallpfosten",
            pfostenartAuswahlwertIndividualisiert: false,
            montageadapterAuswahlwert: "Montageadapter_ohne",
            verbundankerAuswahlwert: "Verbundanker-Set_mit",
            ummantelungAuswahlwert: "Ummantelung_ohne",
            pfostenkappeAuswahlwert: "3840 SYSTEM Licht Pfostenkappe Universal",
            ankerInnen: false,
            ausgleichGefälle: false,
          },
          {
            type: "Element",
            breite: 2.38,
            höhe: 0.95,
            snap: 1,
            isBreiteKonfigurierbar: false,
            x0: 1.3,
            füllungAuswahlwert: "AROS Vorgarten Anthrazit",
            füllungAuswahlwertIndividualisiert: false,
            gehflügelAuswahlwert: "Gehflügel_links",
            drückerAuswahlwert: "Drücker_Knauf_edelstahl",
            profilzylinderAuswahlwert: "Profilzylinder_mit",
            öffnungAuswahlwert: "Öffnung_nach_innen",
            anschlagAuswahlwert: "Anschlag_links",
            schieberichtungAuswahlwert: "Schieberichtung_links",
            schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
            anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilAuswahlwertIndividualisiert: false,
            zubehörAnzahl: {
              "Elektrisches Bodenverriegelungs-Set": 0,
              Handsender: 0,
              "Handsender Design": 0,
              Schlüsselschalter: 0,
              "Funk-Codeschloss": 0,
              "Bluetooth Schalter": 0,
              "Connect Gateway": 0,
              Lichtschranke: 0,
              "Techniker Key": 0,
            },
            toröffnerAuswahlwert: "Toröffner_ohne",
            torschließerAuswahlwert: "Torschließer_ohne",
            form: "Rechteck",
            massAuswahlwert: "240x95_Rechteck_Easy",
            profilPosition: null,
            profilPositionIndividualisiert: false,
            showProfil: false,
            anschlussAufRechts: false,
            gehflügelbreite: 0,
            standflügelbreite: 0,
            torIstFertig: false,
            elementNr: 1,
            isStandardMass: true,
            detailSerie: "AROS Vorgarten",
            anMauer: "AnMauer_nein",
            isMasselement: false,
            isBestellseite: false,
            elementID: 0,
          },
          {
            type: "Pfosten",
            x0: 3.685,
            pfostenfarbeAuswahlwert: "Anthrazit",
            pfostenfarbeAuswahlwertIndividualisiert: false,
            bodenbefestigungAuswahlwert: "Aufgeschraubt auf festem Boden",
            bodenbefestigungAuswahlwertIndividualisiert: false,
            elementhalterAuswahlwert: "2096 Elementhalter",
            elementhalterAuswahlwertIndividualisiert: false,
            pfostenartAuswahlwert: "Metalltorpfosten",
            pfostenartAuswahlwertIndividualisiert: false,
            verbundankerAuswahlwert: "Verbundanker-Set_mit",
            ummantelungAuswahlwert: "Ummantelung_ohne",
            pfostenkappeAuswahlwert: "3840 SYSTEM Licht Pfostenkappe Universal",
            ankerInnen: false,
            ausgleichGefälle: false,
            pfostenAuswahlwert: "Torpfosten Aufschrauben Anthrazit",
          },
          {
            type: "Element",
            breite: 0.98,
            höhe: 0.95,
            snap: 1,
            isBreiteKonfigurierbar: true,
            x0: 3.7775,
            füllungAuswahlwert: "AROS Vorgarten Anthrazit",
            füllungAuswahlwertIndividualisiert: false,
            gehflügelAuswahlwert: "Gehflügel_links",
            drückerAuswahlwert: "Drücker_Klinke_anthrazit",
            profilzylinderAuswahlwert: "Profilzylinder_mit",
            öffnungAuswahlwert: "Öffnung_nach_innen",
            anschlagAuswahlwert: "Anschlag_rechts",
            schieberichtungAuswahlwert: "Schieberichtung_links",
            schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
            anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilAuswahlwertIndividualisiert: false,
            zubehörAnzahl: {
              "Elektrisches Bodenverriegelungs-Set": 0,
              Handsender: 0,
              "Handsender Design": 0,
              Schlüsselschalter: 0,
              "Funk-Codeschloss": 0,
              "Bluetooth Schalter": 0,
              "Connect Gateway": 0,
              Lichtschranke: 0,
              "Techniker Key": 0,
            },
            toröffnerAuswahlwert: "Toröffner_ohne",
            torschließerAuswahlwert: "Torschließer_ohne",
            form: "Einzeltor",
            massAuswahlwert: "Einzeltor_Vorgarten_Easy",
            profilPosition: null,
            profilPositionIndividualisiert: false,
            showProfil: false,
            anschlussAufRechts: false,
            gehflügelbreite: 0,
            standflügelbreite: 0,
            torIstFertig: true,
            elementNr: 2,
            isStandardMass: true,
            detailSerie: "AROS Vorgarten",
            anMauer: "AnMauer_nein",
            isMasstor: false,
            isMasselement: false,
            isMassProfil: false,
            isBestellseite: false,
            isEAntrieb: false,
            elementID: 0,
          },
          {
            type: "Pfosten",
            x0: 4.77,
            pfostenfarbeAuswahlwert: "Anthrazit",
            pfostenfarbeAuswahlwertIndividualisiert: false,
            bodenbefestigungAuswahlwert: "Aufgeschraubt auf festem Boden",
            bodenbefestigungAuswahlwertIndividualisiert: false,
            elementhalterAuswahlwert: "2096 Elementhalter",
            elementhalterAuswahlwertIndividualisiert: false,
            pfostenartAuswahlwert: "Metalltorpfosten",
            pfostenartAuswahlwertIndividualisiert: false,
            verbundankerAuswahlwert: "Verbundanker-Set_mit",
            ummantelungAuswahlwert: "Ummantelung_ohne",
            pfostenkappeAuswahlwert: "3840 SYSTEM Licht Pfostenkappe Universal",
            ankerInnen: false,
            ausgleichGefälle: false,
            pfostenAuswahlwert: "Torpfosten Aufschrauben Anthrazit",
          },
          {
            type: "Element",
            breite: 2.38,
            höhe: 0.95,
            snap: 1,
            isBreiteKonfigurierbar: false,
            x0: 4.855,
            füllungAuswahlwert: "AROS Vorgarten Anthrazit",
            füllungAuswahlwertIndividualisiert: false,
            gehflügelAuswahlwert: "Gehflügel_links",
            drückerAuswahlwert: "Drücker_Knauf_edelstahl",
            profilzylinderAuswahlwert: "Profilzylinder_mit",
            öffnungAuswahlwert: "Öffnung_nach_innen",
            anschlagAuswahlwert: "Anschlag_links",
            schieberichtungAuswahlwert: "Schieberichtung_links",
            schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
            anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilAuswahlwertIndividualisiert: false,
            zubehörAnzahl: {
              "Elektrisches Bodenverriegelungs-Set": 0,
              Handsender: 0,
              "Handsender Design": 0,
              Schlüsselschalter: 0,
              "Funk-Codeschloss": 0,
              "Bluetooth Schalter": 0,
              "Connect Gateway": 0,
              Lichtschranke: 0,
              "Techniker Key": 0,
            },
            toröffnerAuswahlwert: "Toröffner_ohne",
            torschließerAuswahlwert: "Torschließer_ohne",
            form: "Rechteck",
            massAuswahlwert: "240x95_Rechteck_Easy",
            profilPosition: null,
            profilPositionIndividualisiert: false,
            showProfil: false,
            anschlussAufRechts: false,
            gehflügelbreite: 0,
            standflügelbreite: 0,
            torIstFertig: false,
            elementNr: 3,
            isStandardMass: true,
            detailSerie: "AROS Vorgarten",
            anMauer: "AnMauer_nein",
            isMasselement: false,
            isBestellseite: false,
            elementID: 0,
          },
          {
            type: "Pfosten",
            x0: 7.24,
            pfostenfarbeAuswahlwert: "Anthrazit",
            pfostenfarbeAuswahlwertIndividualisiert: false,
            bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
            bodenbefestigungAuswahlwertIndividualisiert: false,
            elementhalterAuswahlwert: "2096 Elementhalter",
            elementhalterAuswahlwertIndividualisiert: false,
            pfostenartAuswahlwert: "Metalltorpfosten",
            pfostenartAuswahlwertIndividualisiert: false,
            verbundankerAuswahlwert: "Verbundanker-Set_mit",
            ummantelungAuswahlwert: "Ummantelung_ohne",
            pfostenkappeAuswahlwert: "3840 SYSTEM Licht Pfostenkappe Universal",
            ankerInnen: false,
            ausgleichGefälle: false,
            pfostenAuswahlwert: "Torpfosten Einbetonieren Anthrazit",
          },
          {
            type: "Element",
            breite: 3.11,
            höhe: 0.95,
            snap: 1,
            isBreiteKonfigurierbar: true,
            x0: 7.335,
            füllungAuswahlwert: "AROS Vorgarten Anthrazit",
            füllungAuswahlwertIndividualisiert: false,
            gehflügelAuswahlwert: "Gehflügel_links",
            drückerAuswahlwert: "Drücker_Knauf_anthrazit",
            profilzylinderAuswahlwert: "Profilzylinder_mit",
            öffnungAuswahlwert: "Öffnung_nach_innen",
            anschlagAuswahlwert: "Anschlag_links",
            schieberichtungAuswahlwert: "Schieberichtung_links",
            schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
            anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilAuswahlwertIndividualisiert: false,
            eAntriebAuswahlwert: "E_Antrieb_ohne",
            zubehörAnzahl: {
              "Elektrisches Bodenverriegelungs-Set": 0,
              Handsender: 0,
              "Handsender Design": 0,
              Schlüsselschalter: 0,
              "Funk-Codeschloss": 0,
              "Bluetooth Schalter": 0,
              "Connect Gateway": 0,
              Lichtschranke: 0,
              "Techniker Key": 0,
            },
            toröffnerAuswahlwert: "Toröffner_ohne",
            form: "Doppeltor",
            massAuswahlwert: "Doppeltor_Vorgarten_Easy",
            profilPosition: null,
            profilPositionIndividualisiert: false,
            showProfil: false,
            anschlussAufRechts: false,
            gehflügelbreite: 1.55,
            standflügelbreite: 1.55,
            torIstFertig: true,
            elementNr: 4,
            isStandardMass: true,
            detailSerie: "AROS Vorgarten",
            anMauer: "AnMauer_nein",
            isMasstor: false,
            isMasselement: false,
            isMassProfil: false,
            isBestellseite: false,
            isEAntrieb: false,
            elementID: 0,
          },
          {
            type: "Pfosten",
            x0: 10.46,
            pfostenfarbeAuswahlwert: "Anthrazit",
            pfostenfarbeAuswahlwertIndividualisiert: false,
            bodenbefestigungAuswahlwert: "Einbetoniert im Boden",
            bodenbefestigungAuswahlwertIndividualisiert: false,
            elementhalterAuswahlwert: "2096 Elementhalter",
            elementhalterAuswahlwertIndividualisiert: false,
            pfostenartAuswahlwert: "Metalltorpfosten",
            pfostenartAuswahlwertIndividualisiert: false,
            verbundankerAuswahlwert: "Verbundanker-Set_mit",
            ummantelungAuswahlwert: "Ummantelung_ohne",
            pfostenkappeAuswahlwert: "3840 SYSTEM Licht Pfostenkappe Universal",
            ankerInnen: false,
            ausgleichGefälle: false,
            pfostenAuswahlwert: "Torpfosten Einbetonieren Anthrazit",
          },
          {
            type: "Element",
            breite: 2.38,
            höhe: 0.95,
            snap: 1,
            isBreiteKonfigurierbar: false,
            x0: 10.545,
            füllungAuswahlwert: "AROS Vorgarten Anthrazit",
            füllungAuswahlwertIndividualisiert: false,
            gehflügelAuswahlwert: "Gehflügel_links",
            drückerAuswahlwert: "Drücker_Knauf_edelstahl",
            profilzylinderAuswahlwert: "Profilzylinder_mit",
            öffnungAuswahlwert: "Öffnung_nach_innen",
            anschlagAuswahlwert: "Anschlag_links",
            schieberichtungAuswahlwert: "Schieberichtung_links",
            schiebetormotorseiteAuswahlwert: "Schiebetormotorseite_A",
            füllungsseiteAuswahlwert: "Füllungsseite_B",
            anschlagprofilAuswahlwert: "Tor_an_Mauer_Anschlagsprofil_180cm",
            anschlagprofilfarbeAuswahlwert: "3574_Montageplatten Anthrazit",
            abschlussleisteAuswahlwertIndividualisiert: false,
            profilAuswahlwertIndividualisiert: false,
            zubehörAnzahl: {
              "Elektrisches Bodenverriegelungs-Set": 0,
              Handsender: 0,
              "Handsender Design": 0,
              Schlüsselschalter: 0,
              "Funk-Codeschloss": 0,
              "Bluetooth Schalter": 0,
              "Connect Gateway": 0,
              Lichtschranke: 0,
              "Techniker Key": 0,
            },
            toröffnerAuswahlwert: "Toröffner_ohne",
            torschließerAuswahlwert: "Torschließer_ohne",
            form: "Rechteck",
            massAuswahlwert: "240x95_Rechteck_Easy",
            profilPosition: null,
            profilPositionIndividualisiert: false,
            showProfil: false,
            anschlussAufRechts: false,
            gehflügelbreite: 0,
            standflügelbreite: 0,
            torIstFertig: false,
            elementNr: 5,
            isStandardMass: true,
            detailSerie: "AROS Vorgarten",
            anMauer: "AnMauer_nein",
            isMasselement: false,
            isBestellseite: false,
            elementID: 0,
          },
        ],
        verteilungsstrategieAuswahlwert: "Individuell",
      },
    ],
    ecken: [
      {
        position: {
          x: -6.4825,
          y: 0,
          z: 0,
        },
      },
      {
        position: {
          x: 6.4825,
          y: 0,
          z: 0,
        },
        pfosten: {
          type: "Pfosten",
          position: {
            x: 6.4825,
            y: 0,
            z: 0,
          },
          pfostenfarbeAuswahlwert: "Anthrazit",
          pfostenfarbeAuswahlwertIndividualisiert: false,
          bodenbefestigungAuswahlwert: "Aufgeschraubt auf festem Boden",
          bodenbefestigungAuswahlwertIndividualisiert: false,
          elementhalterAuswahlwert: "2096 Elementhalter",
          elementhalterAuswahlwertIndividualisiert: false,
          pfostenartAuswahlwert: "Metallpfosten",
          pfostenartAuswahlwertIndividualisiert: false,
          montageadapterAuswahlwert: "Montageadapter_ohne",
          verbundankerAuswahlwert: "Verbundanker-Set_mit",
          ummantelungAuswahlwert: "Ummantelung_ohne",
          pfostenkappeAuswahlwert: "3840 SYSTEM Licht Pfostenkappe Universal",
          ankerInnen: false,
          ausgleichGefälle: false,
        },
      },
    ],
    aufbauten: [
      {
        Key: "h4ah6jlexctj",
        aufbauMeta: "Trafo",
        position: {
          x: 0.21250000000009384,
          y: 0,
          z: -0.18000000000000022,
        },
        size: {
          x: 0.1,
          y: 0.1,
          z: 0.3,
        },
        drehung: 0,
      },
    ],
    lichtleistenSplitter: 0,
    lichtleistenZubehör: {
      "Edelstahl-Blende_Lichtleiste_Steckzaun": 0,
      "3834 SYSTEM Licht Smart Control Set": 1,
      "3836 SYSTEM Licht Fernbedienung": 0,
      "3835 SYSTEM Licht Bewegungsmelder": 0,
      "3837 SYSTEM Licht Steuerungsadapter": 0,
      "3838 SYSTEM Licht Kabel Zusatz-Steuerungsadapter": 0,
    },
    pfostenartAuswahlwert: "Metalltorpfosten",
    pfostenfarbeAuswahlwert: "Anthrazit",
    bodenbefestigungAuswahlwert: "Aufgeschraubt auf festem Boden",
    elementhalterAuswahlwert: "2096 Elementhalter",
    verteilungsstrategieAuswahlwert: "StandardLinks",
    verbundankerAuswahlwert: "Verbundanker-Set_mit",
    pageName: "Zauneditor",
  });
  agt.update3d();
  assertWarnungen(agt, []);
  assert.equals(1, agt.lichtleistenSplitter, "lichtleistenSplitter");
  assert.equals(2, agt.kabel.length, "Anzahl Kabel");
  assert.equals(
    1,
    agt.kabel.filter((k) => k.startAnschluss === "Trafo").length,
    "Genau ein Trafo-Kabel"
  );
  assert.equals(
    0,
    _.sumBy(agt.kabel, (k) => k.schlaufen),
    "Anzahl Schlaufen über alle Kabel"
  );
  assert.isTrue(_.sumBy(agt.kabel, (k) => k.länge) < 14, "Gesamtkabellänge kleiner 14");
};
